/**
 * Nuxt plugin for loading the Alhena (Gleen) chat widget SDK from Amplience CMS.
 *
 * Configuration (`document.gleenConfig`) and the SDK script are injected via
 * {@link useAlhenaChat} when the CMS entry is enabled for the current brand/locale.
 *
 * Once loaded, the SDK exposes `window.gleenWidget` with:
 *   - `sendMessage(message: string)` — open the widget and submit a question
 *   - `open()` — open the widget without a pre-filled question
 *   - `on(event, callback)` — subscribe to widget interaction events
 *
 * @see https://app.alhena.ai
 */

export default defineNuxtPlugin(() => {
  if (!import.meta.client) return

  const { init, isAlhenaChatEnabled, isAlhenaChatResolved } = useAlhenaChat()
  const { waitForWidgetAndSetupTracking } = useAlhenaChatTracking()
  const { currentWebsite } = useWebsite()
  const localePath = useLocalePath()

  // Not awaited: init() fetches the chat config before deciding whether to
  // inject the SDK, so holding the app mount for it bought nothing. `immediate`
  // makes the first call the explicit await used to make. The catch is required
  // because an unawaited rejection would no longer surface as a plugin error.
  //
  // The customer service layout does read the decision, to filter a chat entry
  // out of its contact list, so it needs to tell "off" apart from "not answered
  // yet". finally, not then: init is async, so even a synchronous failure inside
  // it arrives here as a rejection, and a flag that could stay false forever
  // would leave that list hidden rather than merely late.
  // A superseded call (init resolved false) leaves tracking and the resolved flag to the newer one.
  const bootstrap = () =>
    init()
      .then(isCurrent => {
        if (isCurrent && isAlhenaChatEnabled.value)
          waitForWidgetAndSetupTracking()
        return isCurrent
      })
      .catch(err => {
        console.error('Alhena chat init failed', err)
        return true
      })
      .then(isCurrent => {
        if (isCurrent) isAlhenaChatResolved.value = true
      })

  watch([currentWebsite, () => localePath('')], () => bootstrap(), {
    immediate: true,
  })
})
